import { useCallback, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { get } from "lodash"
import { GTM_POST_LOCATION, GTM_VIEW_POST_DETAIL } from "constant"
import { ImdfFeature } from "providers/venue/types"
import { getPostDetail } from "utils/feature"
import { getDisplayDateTime } from "utils/date"
import { pushDataLayer } from "utils/googletagmanager"
import { useGetPost } from "providers/venue/modules/data/posts"

export const usePostDetail = (postId: string) => {
  const { data } = useGetPost(postId)
  const navigate = useNavigate()

  const post = useMemo(() => {
    const feature = data as unknown as ImdfFeature
    if (!feature?.properties) return
    const postDetail = getPostDetail(feature)
    return {
      ...postDetail,
      feature,
      publishedDate: getDisplayDateTime(
        postDetail.publishedAt,
        postDetail.publishedAt
      ),
      durationDate: getDisplayDateTime(
        postDetail.startDate,
        postDetail.endDate || postDetail.startDate
      ),
    }
  }, [data])

  const openExternalLink = useCallback(
    () => post?.externalLink && window.open(post?.externalLink, "_blank"),
    [post?.externalLink]
  )

  useEffect(() => {
    if (post) {
      pushDataLayer({
        event: GTM_VIEW_POST_DETAIL,
        post_id: get(post, "id"),
        post_type: get(post, "type"),
        post_category: get(post, "category.en"),
        post_title: get(post, "title.en"),
      })
    }
  }, [post])

  const navigateToLocation = useCallback(() => {
    const pathToNavigate = post?.locationUrl

    if (!pathToNavigate) return

    pushDataLayer({
      event: GTM_POST_LOCATION,
      post_id: get(post, "id"),
      post_location_name: get(post.location, "en"),
      post_type: get(post, "type"),
      post_category: get(post, "category.en"),
      post_title: get(post, "title.en"),
      post_location_url: pathToNavigate,
    })
    navigate(pathToNavigate)
  }, [navigate, post])

  return {
    post,
    navigateToLocation,
    openExternalLink,
  }
}
